<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useNavLabels } from '../../navigation/useNavLabels'

import { useNavigation } from '@/app/navigation'
import {
  flattenNavigationDestinations,
  searchNavigationDestinations,
} from '@/app/navigation/searchDestinations'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui/feedback'
import { Icon } from '@/shared/ui/icon'

const route = useRoute()
const router = useRouter()
const { items } = useNavigation(() => route)
const { labelOf } = useNavLabels()
const { t } = useI18n()
const toast = useToast()
const query = ref('')
const open = ref(false)
const activeIndex = ref(-1)

const results = computed(() => {
  return searchNavigationDestinations(
    flattenNavigationDestinations(items.value, labelOf),
    query.value,
  )
})

function update(): void {
  open.value = query.value.trim().length > 0
  activeIndex.value = results.value.length > 0 ? 0 : -1
}

function close(): void {
  query.value = ''
  open.value = false
  activeIndex.value = -1
}

async function select(result: (typeof results.value)[number]): Promise<void> {
  // An action leaf (Orphex MCP) acts in place of navigating; still inside the click or Enter, so
  // the tab it opens gets past a popup blocker.
  if (result.action) {
    close()
    void result.action()
    return
  }
  if (!result.target || !router.hasRoute(result.target.name)) {
    toast.warning(t('shell.topbar.destinationUnavailable'))
    return
  }
  close()
  await router.push(result.target)
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    open.value = false
    activeIndex.value = -1
    return
  }
  if (!open.value || results.value.length === 0) return
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    activeIndex.value = (activeIndex.value + 1) % results.value.length
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    activeIndex.value = (activeIndex.value - 1 + results.value.length) % results.value.length
  } else if (event.key === 'Enter' && activeIndex.value >= 0) {
    event.preventDefault()
    const result = results.value[activeIndex.value]
    if (result) void select(result)
  }
}
</script>

<template>
  <div
    class="topbar-search relative w-[min(100%,var(--ui-shell-topbar-search-width))] max-[56rem]:hidden"
  >
    <label
      class="topbar-search__field flex h-[var(--ui-shell-topbar-search-height)] min-w-48 w-full items-center gap-xs rounded-[var(--ui-shell-topbar-search-radius)] bg-surface-sunken px-sm"
    >
      <span class="topbar-search__sr-only sr-only">{{ t('shell.topbar.searchLabel') }}</span>
      <Icon name="search" weight="regular" :size="14" aria-hidden="true" />
      <input
        v-model="query"
        class="w-full min-w-0 border-0 bg-transparent leading-4 text-xs placeholder:text-xs outline-0 placeholder:italic [font:inherit]"
        type="search"
        role="combobox"
        aria-autocomplete="list"
        aria-controls="topbar-navigation-results"
        :aria-expanded="open"
        :aria-activedescendant="
          activeIndex >= 0 ? `topbar-navigation-result-${activeIndex}` : undefined
        "
        :placeholder="t('shell.topbar.searchPlaceholder')"
        :aria-label="t('shell.topbar.searchLabel')"
        @input="update"
        @focus="update"
        @keydown="onKeydown"
      />
    </label>
    <ul
      v-if="open"
      id="topbar-navigation-results"
      class="topbar-search__results absolute left-0 right-0 top-[calc(100%+var(--ui-space-2xs))] z-sticky-raised m-0 grid list-none rounded-md border border-border bg-surface p-2xs shadow-pop"
      role="listbox"
    >
      <li
        v-for="(result, index) in results"
        :id="`topbar-navigation-result-${index}`"
        :key="result.id"
        role="option"
        :aria-selected="index === activeIndex"
      >
        <button
          type="button"
          class="w-full cursor-pointer rounded-sm border-0 bg-transparent px-sm py-xs text-left text-fg [font:inherit] hover:bg-surface-sunken"
          :class="{ 'bg-surface-sunken': index === activeIndex }"
          @mousedown.prevent
          @click="select(result)"
        >
          {{ result.label }}
        </button>
      </li>
      <li v-if="results.length === 0" class="topbar-search__empty px-sm py-xs text-xs text-muted">
        {{ t('shell.topbar.searchNoResults') }}
      </li>
    </ul>
  </div>
</template>
